'use client';

import { useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { processMarkdown } from '@/lib/utils';
import Link from 'next/link';
import { List } from 'lucide-react';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

function extractHeadings(markdown: string): TocItem[] {
  const headings: TocItem[] = [];
  const lines = markdown.split('\n');
  for (const line of lines) {
    const match = line.match(/^(#{2,3})\s+(.+)/);
    if (match) {
      const level = match[1].length;
      const text = match[2].replace(/[*_`\[\]]/g, '').trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      headings.push({ id, text, level });
    }
  }
  return headings;
}

function FloatingToc({ headings }: { headings: TocItem[] }) {
  const [isOpen, setIsOpen] = useState(false);

  if (headings.length < 3) return null;

  return (
    <div className="fixed bottom-6 right-6 z-40">
      {isOpen && (
        <div className="absolute bottom-14 right-0 w-64 max-h-[60vh] overflow-y-auto glass-panel rounded-lg p-3 shadow-lg">
          <h4 className="text-xs font-bold text-primary mb-2 uppercase tracking-wider" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            Contents
          </h4>
          <nav className="space-y-1">
            {headings.map(h => (
              <a
                key={h.id}
                href={`#${h.id}`}
                onClick={() => setIsOpen(false)}
                className={`block text-xs text-muted-foreground hover:text-primary transition-colors ${h.level === 3 ? 'pl-3' : ''}`}
              >
                {h.text}
              </a>
            ))}
          </nav>
        </div>
      )}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:bg-primary/90 transition-colors"
      >
        <List className="h-4 w-4" />
      </button>
    </div>
  );
}

interface WikiContentProps {
  content: string;
  title: string;
  tags?: string[];
  updatedAt?: string;
  showEditButton?: boolean;
  slug?: string;
}

export function WikiContent({ 
  content, 
  title, 
  tags = [], 
  updatedAt,
  showEditButton = false,
  slug 
}: WikiContentProps) {
  const processedContent = processMarkdown(content);
  const headings = useMemo(() => extractHeadings(content), [content]);

  return (
    <>
    <article className="max-w-[720px]">
      <header className="mb-8">
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">{title}</h1>
          {showEditButton && slug && (
            <Link
              href={`/admin/edit/${slug}`}
              className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              Edit
            </Link>
          )}
        </div>
        {tags.length > 0 && (
          <div className="flex gap-2 mt-4">
            {tags.map((tag) => (
              <span
                key={tag}
                className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        {updatedAt && (
          <p className="text-sm text-muted-foreground mt-2">
            Last updated: {new Date(updatedAt).toLocaleDateString()}
          </p>
        )}
      </header>
      
      <div className="prose prose-lg dark:prose-invert max-w-none">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={{
            h2: ({ children }) => {
              const text = String(children).replace(/[*_`\[\]]/g, '').trim();
              const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
              return <h2 id={id}>{children}</h2>;
            },
            h3: ({ children }) => {
              const text = String(children).replace(/[*_`\[\]]/g, '').trim();
              const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
              return <h3 id={id}>{children}</h3>;
            },
            a: ({ href, children }) => {
              if (href?.startsWith('/wiki/')) {
                return (
                  <Link href={href} className="wiki-link">
                    {children}
                  </Link>
                );
              }
              return (
                <a href={href} target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              );
            },
            div: ({ className, children, ...props }) => {
              if (className === 'infobox') {
                return (
                  <div className="infobox" {...props}>
                    {children}
                  </div>
                );
              }
              return <div className={className} {...props}>{children}</div>;
            },
          }}
        >
          {processedContent}
        </ReactMarkdown>
      </div>
    </article>
    <FloatingToc headings={headings} />
    </>
  );
}
